// InlineFormatToolbar — floating toolbar that follows the text selection
// inside a contentEditable region (currently used by ParagraphBlockEditor).
//
// Buttons: Bold / Italic / Underline / Strike / Link / Code.
// Implementation uses document.execCommand — deprecated but still
// universally supported and trivial to wire to contentEditable. The
// paragraph editor afterwards runs sanitizeInlineHtml() to keep the
// stored HTML on-spec.
//
// Positioning: absolute over the editable element, aligned to the
// selection's bounding rect. Hidden when selection is collapsed or
// outside the host.

import { useEffect, useRef, useState } from "react";
import { t } from "./theme";

export interface InlineFormatToolbarProps {
  // Element whose selection we track. Toolbar hides if selection is
  // outside this host.
  host: HTMLElement | null;
  // Called after any command — host should re-read innerHTML and re-sanitize.
  onAfterCommand: () => void;
}

interface Rect {
  top: number;
  left: number;
}

export function InlineFormatToolbar({ host, onAfterCommand }: InlineFormatToolbarProps) {
  const [pos, setPos] = useState<Rect | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!host) return;
    function updatePos() {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
        setPos(null);
        return;
      }
      const range = sel.getRangeAt(0);
      const node = range.commonAncestorContainer;
      const anchorEl = (
        node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement
      ) as HTMLElement | null;
      if (!anchorEl || !host || !host.contains(anchorEl)) {
        setPos(null);
        return;
      }
      const rect = range.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        setPos(null);
        return;
      }
      const hostRect = host.getBoundingClientRect();
      setPos({
        top: rect.top - hostRect.top - 40,
        left: rect.left - hostRect.left + rect.width / 2,
      });
    }
    document.addEventListener("selectionchange", updatePos);
    return () => document.removeEventListener("selectionchange", updatePos);
  }, [host]);

  if (!pos) return null;

  // Run an execCommand and ping the host to read back HTML.
  function exec(cmd: string, value?: string) {
    document.execCommand(cmd, false, value);
    onAfterCommand();
  }

  function applyLink() {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return;
    const existing = (() => {
      const anchor = sel.anchorNode?.parentElement?.closest("a");
      return anchor ? anchor.getAttribute("href") : "";
    })();
    const url = prompt("Link URL (http/https):", existing || "https://");
    if (url == null) return;
    const v = url.trim();
    if (!v) {
      exec("unlink");
      return;
    }
    if (!/^https?:\/\//i.test(v)) {
      alert("Link must start with http:// or https://");
      return;
    }
    exec("createLink", v);
  }

  return (
    <div
      ref={ref}
      onMouseDown={(e) => e.preventDefault() /* keep selection */}
      style={{
        position: "absolute",
        top: pos.top,
        left: pos.left,
        transform: "translateX(-50%)",
        display: "flex",
        gap: 2,
        padding: 3,
        background: t.card,
        border: `1px solid ${t.border}`,
        borderRadius: 6,
        boxShadow: "0 4px 12px rgba(0,0,0,0.10)",
        zIndex: 20,
      }}
    >
      <FmtBtn label="B" title="Bold" bold onClick={() => exec("bold")} />
      <FmtBtn label="I" title="Italic" italic onClick={() => exec("italic")} />
      <FmtBtn label="U" title="Underline" underline onClick={() => exec("underline")} />
      <FmtBtn label="S" title="Strikethrough" strike onClick={() => exec("strikeThrough")} />
      <FmtBtn label="🔗" title="Link" onClick={applyLink} />
      <FmtBtn
        label="</>"
        title="Inline code"
        mono
        onClick={() => {
          // execCommand has no 'code' — wrap selection with <code>.
          const sel = window.getSelection();
          if (!sel || sel.isCollapsed || sel.rangeCount === 0) return;
          const range = sel.getRangeAt(0);
          const code = document.createElement("code");
          try {
            code.appendChild(range.extractContents());
            range.insertNode(code);
            sel.removeAllRanges();
            const r2 = document.createRange();
            r2.selectNodeContents(code);
            sel.addRange(r2);
          } catch {
            /* ignore */
          }
          onAfterCommand();
        }}
      />
    </div>
  );
}

function FmtBtn({
  label,
  title,
  onClick,
  bold,
  italic,
  underline,
  strike,
  mono,
}: {
  label: string;
  title: string;
  onClick: () => void;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  mono?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      style={{
        minWidth: 26,
        height: 26,
        padding: "0 6px",
        background: "transparent",
        border: "none",
        borderRadius: 4,
        color: t.text2,
        fontFamily: mono ? t.mono : t.sans,
        fontSize: 12,
        fontWeight: bold ? 700 : 500,
        fontStyle: italic ? "italic" : "normal",
        textDecoration: [
          underline ? "underline" : "",
          strike ? "line-through" : "",
        ]
          .filter(Boolean)
          .join(" ") || "none",
        cursor: "pointer",
        transition: t.transition,
      }}
      onMouseEnter={(e) =>
        ((e.currentTarget as HTMLButtonElement).style.background = t.hover)
      }
      onMouseLeave={(e) =>
        ((e.currentTarget as HTMLButtonElement).style.background =
          "transparent")
      }
    >
      {label}
    </button>
  );
}
